/**
 * Photo URL Cache Utility
 * 
 * Caches presigned photo URLs to avoid downloading the same image multiple times.
 * When the backend generates a new presigned URL on each fetch, the browser treats
 * it as a new resource even though it's the same image. This cache reuses URLs
 * across component re-renders and page navigations.
 * 
 * Cache Strategy:
 * - Store URLs with 6-hour client-side expiry (backend URLs expire in 7 days)
 * - Clear cache on page reload (uses in-memory Map)
 * - Keyed by pet ID for easy lookup
 */

interface CachedPhotoUrl {
  url: string;
  expiry: number;  // Unix timestamp in milliseconds
}

class PhotoUrlCache {
  private cache: Map<string, CachedPhotoUrl>;
  private defaultTtl: number;  // 6 hours in milliseconds

  constructor(ttlHours: number = 6) {
    this.cache = new Map();
    this.defaultTtl = ttlHours * 60 * 60 * 1000;
  }

  /**
   * Get a cached photo URL if it exists and hasn't expired
   */
  get(petId: string): string | null {
    const cached = this.cache.get(petId);
    
    if (!cached) {
      return null;
    }

    // Check if expired
    if (Date.now() > cached.expiry) {
      this.cache.delete(petId);
      return null;
    }

    return cached.url;
  }

  /**
   * Store a photo URL in the cache
   */
  set(petId: string, url: string, ttlMs?: number): void {
    const expiry = Date.now() + (ttlMs || this.defaultTtl);
    this.cache.set(petId, { url, expiry });
  }

  /**
   * Get or set a photo URL
   * Returns the cached URL if available, otherwise caches and returns the new URL
   */
  getOrSet(petId: string, newUrl: string | null | undefined): string | null {
    if (!newUrl) {
      return null;
    }

    // Try to get cached URL first
    const cached = this.get(petId);
    if (cached) {
      return cached;
    }

    // Cache the new URL and return it
    this.set(petId, newUrl);
    return newUrl;
  }

  /**
   * Clear a specific pet's cached URL
   */
  clear(petId: string): void {
    this.cache.delete(petId);
  }

  /**
   * Clear all cached URLs
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics (for debugging)
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    return {
      totalEntries: entries.length,
      activeEntries: entries.filter(([_, v]) => v.expiry > now).length,
      expiredEntries: entries.filter(([_, v]) => v.expiry <= now).length
    };
  }
}

// Global singleton instance
export const photoUrlCache = new PhotoUrlCache(6);  // 6 hour TTL

// Export class for testing
export { PhotoUrlCache };

